import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo } from 'react';
import { businessUnitHooks, portfolioHooks, sectorHooks } from '../Org/hooks';
import { tenantEnumSelectOptions, useTenantEnumValues } from '../Programs/referenceDataHooks';
// The command-center scope filter. Cascades sector → business unit →
// portfolio; each forwards to ryugu's rollup RPCs (which accept the org-node
// ids directly). Contract state filters on the tenant_enum_values vocabulary
// (domain contract_lifecycle_state) — the RPCs take the enum value id.
const ALL = 'all';
export function FilterBar({ companyId, value, onChange }) {
    const sectors = sectorHooks.useList({
        companyId
    });
    const businessUnits = businessUnitHooks.useList({
        companyId,
        sectorId: value.sectorId || ''
    });
    const portfolios = portfolioHooks.useList({
        companyId,
        businessUnitId: value.businessUnitId || ''
    });
    // Unlike the org hooks above, useTenantEnumValues resolves the tenant
    // internally (useTenantId) rather than taking the companyId prop — same
    // tenant either way today, but don't expect this select to follow a
    // companyId override.
    const lifecycleStates = useTenantEnumValues('contract_lifecycle_state');
    const stateOptions = useMemo(()=>tenantEnumSelectOptions(lifecycleStates.data).map((o)=>({
                id: o.value,
                name: o.label
            })), [
        lifecycleStates.data
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center gap-3",
        "data-testid": Testids.commandCenter.filterBar,
        children: [
            /*#__PURE__*/ _jsx(ScopeSelect, {
                testid: Testids.commandCenter.filterSector,
                placeholder: "All sectors",
                value: value.sectorId,
                options: sectors.data?.items ?? [],
                // Changing sector clears the downstream BU + portfolio scope;
                // contract state is orthogonal to the org hierarchy and survives.
                onChange: (id)=>onChange({
                        ...value,
                        sectorId: id,
                        businessUnitId: '',
                        portfolioId: ''
                    })
            }),
            /*#__PURE__*/ _jsx(ScopeSelect, {
                testid: Testids.commandCenter.filterBusinessUnit,
                placeholder: "All business units",
                value: value.businessUnitId,
                options: businessUnits.data?.items ?? [],
                onChange: (id)=>onChange({
                        ...value,
                        businessUnitId: id,
                        portfolioId: ''
                    })
            }),
            /*#__PURE__*/ _jsx(ScopeSelect, {
                testid: Testids.commandCenter.filterPortfolio,
                placeholder: "All portfolios",
                value: value.portfolioId,
                options: portfolios.data?.items ?? [],
                onChange: (id)=>onChange({
                        ...value,
                        portfolioId: id
                    })
            }),
            /*#__PURE__*/ _jsx(ScopeSelect, {
                testid: Testids.commandCenter.filterContractState,
                placeholder: "All contract states",
                value: value.contractState,
                options: stateOptions,
                onChange: (id)=>onChange({
                        ...value,
                        contractState: id
                    })
            })
        ]
    });
}
function ScopeSelect({ testid, placeholder, value, options, onChange }) {
    // The Select control can't hold an empty-string item, so "all" is the
    // sentinel for "no filter" and maps back to "" on the way out.
    return /*#__PURE__*/ _jsxs(Select, {
        value: value || ALL,
        onValueChange: (v)=>onChange(v === ALL ? '' : v),
        children: [
            /*#__PURE__*/ _jsx(SelectTrigger, {
                className: "w-[200px]",
                "data-testid": testid,
                children: /*#__PURE__*/ _jsx(SelectValue, {
                    placeholder: placeholder
                })
            }),
            /*#__PURE__*/ _jsxs(SelectContent, {
                children: [
                    /*#__PURE__*/ _jsx(SelectItem, {
                        value: ALL,
                        children: placeholder
                    }),
                    options.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                            value: o.id,
                            children: o.name
                        }, o.id))
                ]
            })
        ]
    });
}
